import {
  TextNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  SerializedTextNode,
  Spread,
  NodeKey,
} from "lexical";
import { extractStyles, mergeStyles, styleStringToMap } from "../utils/common";

export type SerializedStyledTextNode = Spread<
  { style: string },
  SerializedTextNode
>;

// ─── StyledTextNode ───────────────────────────────────────────────────────────

export class StyledTextNode extends TextNode {
  __customStyle: string;

  static getType(): string {
    return "styled-text";
  }

  static clone(node: StyledTextNode): StyledTextNode {
    const clone = new StyledTextNode(node.__text, node.__key);
    // Must copy __customStyle — Lexical clones nodes for undo/redo history
    clone.__customStyle = node.__customStyle ?? "";
    return clone;
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
    this.__customStyle = "";
  }

  // ── Serialization (save/restore editor state) ──────────────────────────────

  static importJSON(s: SerializedStyledTextNode): StyledTextNode {
    const node = super.importJSON(s) as StyledTextNode;
    node.__customStyle = s.style ?? "";
    return node;
  }

  exportJSON(): SerializedStyledTextNode {
    return { ...super.exportJSON(), style: this.__customStyle };
  }

  // ── DOM Import (HTML → Lexical nodes) ─────────────────────────────────────

  static importDOM(): DOMConversionMap | null {
    const convert = (domNode: HTMLElement): DOMConversionOutput => {
      const tag = domNode.tagName;
      const style = extractStyles(domNode);

      return {
        // node: null → skip creating a node for the element itself.
        // Lexical walks into children naturally; forChild stamps
        // styles + format flags onto each child TextNode.
        // Without this, you get duplicate nodes (one per element + one per text child).
        node: null,
        forChild: (child) => {
          if (!(child instanceof TextNode)) return child;

          // Promote plain TextNode → StyledTextNode so it can carry custom styles
          const styled =
            child instanceof StyledTextNode
              ? (child.getWritable() as StyledTextNode)
              : new StyledTextNode(child.getTextContent());

          // Merge styles from this element into whatever the child already has
          // This handles nesting: <span style="color:red"><strong>text</strong></span>
          // → styled node gets both color:red AND bold format
          if (style) {
            styled.__customStyle = mergeStyles(
              styled.__customStyle,
              styleStringToMap(style),
            );
          }

          // Apply semantic format flags from tag name
          if (tag === "STRONG" || tag === "B") styled.toggleFormat("bold");
          if (tag === "EM" || tag === "I") styled.toggleFormat("italic");
          if (tag === "U") styled.toggleFormat("underline");
          if (tag === "S" || tag === "STRIKE")
            styled.toggleFormat("strikethrough");
          if (tag === "CODE") styled.toggleFormat("code");

          return styled;
        },
      };
    };

    return {
      span: () => ({ conversion: convert, priority: 2 }),
      strong: () => ({ conversion: convert, priority: 2 }),
      b: () => ({ conversion: convert, priority: 2 }),
      em: () => ({ conversion: convert, priority: 2 }),
      i: () => ({ conversion: convert, priority: 2 }),
      u: () => ({ conversion: convert, priority: 2 }),
      s: () => ({ conversion: convert, priority: 2 }),
      code: () => ({ conversion: convert, priority: 2 }),
    };
  }

  // ── DOM Export (Lexical nodes → HTML) ─────────────────────────────────────

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    // super.exportDOM handles format flags → <strong>, <em>, <u> etc.
    const { element } = super.exportDOM(editor);
    if (element && "setAttribute" in element && this.__customStyle) {
      (element as HTMLElement).setAttribute("style", this.__customStyle);
    }
    return { element };
  }

  // ── DOM Rendering ──────────────────────────────────────────────────────────

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    if (this.__customStyle) {
      dom.setAttribute("style", this.__customStyle);
    }
    return dom;
  }

  updateDOM(
    prevNode: StyledTextNode,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    // super.updateDOM returns true when Lexical needs to fully recreate the DOM
    // node (e.g. format flag changed from bold → not bold requires a new element)
    const mustRecreate = super.updateDOM(prevNode as never, dom, config);
    if (mustRecreate) return true;

    // Only patch the style attribute — no need to recreate the whole element
    if (prevNode.__customStyle !== this.__customStyle) {
      if (this.__customStyle) dom.setAttribute("style", this.__customStyle);
      else dom.removeAttribute("style");
    }
    return false;
  }

  // ── Style API ─────────────────────────────────────────────────────────────

  // Merge one or more CSS properties into the existing style.
  // Pass null as a value to remove a specific property.
  //
  // Examples:
  //   node.patchStyle({ color: "#ff0000" })
  //   node.patchStyle({ "font-size": "18px", "font-family": "Georgia" })
  //   node.patchStyle({ color: null }) // removes color only
  patchStyle(patch: Record<string, string | null>): void {
    const self = this.getWritable() as StyledTextNode;
    self.__customStyle = mergeStyles(self.__customStyle, patch);
  }

  // Get a single CSS property value from the custom style
  getStyleProperty(prop: string): string | undefined {
    return styleStringToMap(this.__customStyle)[prop];
  }

  // Replace entire style string (use patchStyle for incremental updates)
  setCustomStyle(style: string): void {
    const self = this.getWritable() as StyledTextNode;
    self.__customStyle = style;
  }

  // Remove all custom styles
  clearStyle(): void {
    const self = this.getWritable() as StyledTextNode;
    self.__customStyle = "";
  }

  getCustomStyle(): string {
    return this.__customStyle;
  }
}
